import React, {useCallback, useEffect, useState} from 'react';
import {
  Alert,
  Button,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import Select from "react-select";
import {useColumnSelectorStorage} from './columnSelectorStorage';
import PropTypes from 'prop-types';
import './columnSelector.css';
import {closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {SortableColumnItem} from './SortableColumnItem';

/**
 * Modal component for selecting visible columns in a table
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is open
 * @param {function} props.toggle - Function to toggle the modal state
 * @param {string} props.tableId - Unique identifier for the table (used for storage)
 * @param {Array} props.availableColumns - Array of column objects with { id, name, default } properties
 * @param {function} props.onSave - Callback when columns are saved - receives visible columns array
 */
const ColumnSelectorModal = ({isOpen, toggle, tableId, availableColumns, onSave}) => {
  const {getVisibleColumns, saveVisibleColumns} = useColumnSelectorStorage();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [error, setError] = useState(null);

  // Set up sensors for drag and drop with proper configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // The activationConstraint helps prevent accidental drags
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get required columns from the available columns
  const requiredColumns = availableColumns
    .filter(column => column.required)
    .map(column => column.id);

  useEffect(() => {
    if (isOpen) {
      // When modal opens, get the currently visible columns from storage
      // or use default configuration if nothing is stored yet
      const storedColumns = getVisibleColumns(tableId);

      if (storedColumns && storedColumns.length > 0) {
        // Make sure required columns are always included
        let finalColumns = [...storedColumns];

        // Add any missing required columns
        requiredColumns.forEach(requiredCol => {
          if (!finalColumns.includes(requiredCol)) {
            finalColumns.push(requiredCol);
          }
        });

        setSelectedColumns(finalColumns);
      } else {
        // If no stored columns, use columns marked as default and ensure required columns are included
        const defaultColumns = availableColumns
          .filter(column => column.default || requiredColumns.includes(column.id))
          .map(column => column.id);
        setSelectedColumns(defaultColumns);
      }

      // Clear any previous error
      setError(null);
    }
  }, [isOpen, tableId, availableColumns, getVisibleColumns]);

  // Check if a column can be removed
  const canRemoveColumn = (columnId) => {
    return !requiredColumns.includes(columnId);
  };

  const handleRemoveColumn = (columnId) => {
    // Check if this is a required column
    if (requiredColumns.includes(columnId)) {
      setError(`The "${getColumnName(columnId)}" column cannot be removed`);
      return;
    }

    // Remove the column
    setSelectedColumns(prevSelected => prevSelected.filter(id => id !== columnId));
    setError(null);
  };

  const handleAddColumn = (columnId) => {
    setSelectedColumns(prevSelected => [...prevSelected, columnId]);
    setError(null);
  };

  const handleDragEnd = useCallback((event) => {
    const {active, over} = event;

    // Make sure both active and over objects exist and have IDs
    if (!active || !over || !active.id || !over.id) {
      // Silently handle the error
      return;
    }

    if (active.id !== over.id) {
      setSelectedColumns((columns) => {
        const oldIndex = columns.indexOf(active.id);
        const newIndex = columns.indexOf(over.id);

        // Ensure both indexes are valid
        if (oldIndex === -1 || newIndex === -1) {
          return columns; // Return unchanged if we can't find the indexes
        }

        return arrayMove(columns, oldIndex, newIndex);
      });
    }
  }, []);

  const handleSave = () => {
    // Ensure all required columns are included
    let finalColumns = [...selectedColumns];
    let columnsModified = false;

    requiredColumns.forEach(requiredCol => {
      if (!finalColumns.includes(requiredCol)) {
        finalColumns.push(requiredCol);
        columnsModified = true;
      }
    });

    // If we had to add required columns, show a message
    if (columnsModified) {
      setError("Required columns have been automatically added");
      setSelectedColumns(finalColumns);
      return;
    }

    // Check if there's at least one column selected (required or not)
    if (finalColumns.length === 0) {
      setError("You must select at least one column");
      return;
    }

    // Save to local storage
    saveVisibleColumns(tableId, finalColumns);

    // Return the selected columns to the parent component
    onSave(finalColumns);

    // Close the modal
    toggle();
  };

  // Get column name by id
  const getColumnName = (columnId) => {
    const column = availableColumns.find(col => col.id === columnId);
    return column ? column.name : columnId;
  };

  // Get available columns that aren't already selected
  const getAvailableOptions = () => {
    return availableColumns.filter(column => !selectedColumns.includes(column.id));
  };

  return (
    <Modal className={'modal-dialog-centered column-selector-modal'} isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Modify Columns</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        <p>Drag columns to reorder or remove them with the X button. <small className="text-muted">(Required columns
          cannot be removed)</small></p>
        
        {/* Selected columns list with drag and drop */}
        <div className="column-selector-list mb-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedColumns}
              strategy={verticalListSortingStrategy}
            >
              {selectedColumns.map((columnId) => (
                <SortableColumnItem
                  key={columnId}
                  id={columnId}
                  name={getColumnName(columnId)}
                  onRemove={() => handleRemoveColumn(columnId)}
                  required={!canRemoveColumn(columnId)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        
        {/* Column selector outside of the scrollable area */}
        {getAvailableOptions().length > 0 && (
          <div className="column-selector-add-panel">
            <div className="mb-2">
              <label className="form-control-label">Add Column</label>
            </div>
            <div className="column-selector-select-container">
              <Select
                placeholder="Select a column to add..."
                options={getAvailableOptions().map(column => ({
                  value: column.id,
                  label: column.name
                }))}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    handleAddColumn(selectedOption.value);
                  }
                }}
                className="basic-single"
                classNamePrefix="select"
                isClearable={true}
                isSearchable={true}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Cancel</Button>
        <Button color="primary" onClick={handleSave}>Save</Button>
      </ModalFooter>
    </Modal>
  );
};

ColumnSelectorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  tableId: PropTypes.string.isRequired,
  availableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      default: PropTypes.bool,
      required: PropTypes.bool
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired
};

export default ColumnSelectorModal;
